/**
 * DIE MARKEN DES HANDBUCHS - OHNE JSX DANEBEN.
 *
 * ORIGINALTEXT MARVIN (05.09.2026)
 * > „im handbuch alles mit ** gekennzeichnet ist, sollst du die sterne weg
 * >  machen und den text fett schreiben. außerdem sind manche texte nicht gut
 * >  geschrieben, sondern nur von dem alten system übernommen, aber es ist
 * >  keine anleitung und es fehlen auch bilder. also passe es sauber an."
 *
 * **DIESE DATEI TRÄGT KEIN JSX, UND DAS IST DER GANZE ZWECK.** Die Regel stand
 * in `seiten/Handbuch.tsx`, also an einer Stelle, die ein Prüfstand nur mit
 * einer TEXTSUCHE erreicht - und **eine Textsuche misst, dass etwas dasteht,
 * nicht, dass es stimmt.** Genau daran hing die Frage, die Marvin gestellt
 * hat: steht auf dem Bildschirm noch ein Stern? Das lässt sich nicht suchen,
 * das muss man ausrechnen. Achte Datei dieser Art nach `namen.ts`,
 * `terminraster.ts`, `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts`,
 * `kartenebene.ts` und `grenzpunkte.ts`.
 *
 * ES IST ABSICHTLICH KEIN MARKDOWN. Eine Auszeichnungssprache mit Sternchen,
 * Klammern und Rückstrichen ist für jemanden, der einen Satz nachträgt, eine
 * Fehlerquelle - und für den Leser ist ein danebengegangenes Sternchen ein
 * Sternchen im Text. FÜNF Marken, und mehr nicht:
 *
 *   `## `                      eine Zwischenüberschrift
 *   `• `                       ein Punkt
 *   `> `                       ein Merksatz
 *   `[bild: datei | Text]`     ein Bildschirmfoto
 *   `[ablauf: schluessel]`     ein Ablaufplan
 *   `**fett**`                 die fünfte, und die einzige MITTEN in der Zeile
 */

export type Stueck =
  | { art: 'absatz'; text: string }
  | { art: 'ueberschrift'; text: string }
  | { art: 'punkte'; punkte: string[] }
  | { art: 'merksatz'; text: string }
  | { art: 'bild'; datei: string; unter: string }
  | { art: 'ablauf'; schluessel: string };

/** Aus dem Kapiteltext Stücke machen. */
export function zerlege(text: string): Stueck[] {
  const raus: Stueck[] = [];
  let absatz: string[] = [];
  let punkte: string[] = [];

  const absatzZu = () => {
    if (absatz.length) raus.push({ art: 'absatz', text: absatz.join(' ') });
    absatz = [];
  };
  const punkteZu = () => {
    if (punkte.length) raus.push({ art: 'punkte', punkte });
    punkte = [];
  };

  /* EINE MARKE DARF UMBRECHEN. Eine Bildunterschrift ist ein Satz, und wer
     einen Satz in eine Zeile zwingt, bekommt entweder eine 200 Zeichen lange
     Zeile oder eine Marke, die als roher Text dasteht. Beides ist am
     26.08.2026 beim ersten Blick auf das fertige Handbuch passiert. Zeilen,
     die mit `[bild:` oder `[ablauf:` anfangen, werden deshalb bis zur
     schliessenden Klammer eingesammelt. */
  const roheZeilen = String(text ?? '').split('\n');
  const zeilen: string[] = [];
  for (let i = 0; i < roheZeilen.length; i++) {
    let z = roheZeilen[i];
    if (/^\[(bild|ablauf):/i.test(z) && !z.includes(']')) {
      while (i + 1 < roheZeilen.length && !z.includes(']')) {
        i++;
        z += ' ' + roheZeilen[i].trim();
      }
    }
    zeilen.push(z);
  }

  for (const roh of zeilen) {
    const z = roh.trim();

    if (!z) { absatzZu(); punkteZu(); continue; }

    /* EINE MARKE STEHT AM ZEILENANFANG. Eingerückt ist sie ein BEISPIEL, und
       genau eines steht im Kapitel „Dieses Handbuch ändern": dort wird
       gezeigt, wie eine Bildmarke aussieht. Ohne diese Regel sucht die
       Oberfläche eine Datei namens „dateiname.png" und schreibt darunter, dass
       sie fehlt - in dem Kapitel, das erklärt, wie es geht. Prüfstand 038 hat
       genau das gefunden. */
    const eingerueckt = /^\s/.test(roh);

    const bild = eingerueckt
      ? null
      : /^\[bild:\s*([^|\]]+?)\s*(?:\|\s*([^\]]*?))?\s*\]$/i.exec(z);
    if (bild) {
      absatzZu(); punkteZu();
      raus.push({ art: 'bild', datei: bild[1], unter: (bild[2] ?? '').trim() });
      continue;
    }
    const ablauf = eingerueckt
      ? null
      : /^\[ablauf:\s*([^\]]+?)\s*\]$/i.exec(z);
    if (ablauf) {
      absatzZu(); punkteZu();
      raus.push({ art: 'ablauf', schluessel: ablauf[1] });
      continue;
    }
    if (z.startsWith('## ')) {
      absatzZu(); punkteZu();
      raus.push({ art: 'ueberschrift', text: z.slice(3).trim() });
      continue;
    }
    if (z.startsWith('> ')) {
      absatzZu(); punkteZu();
      /* MEHRERE „> "-ZEILEN SIND EIN MERKSATZ, nicht drei. Sonst steht ein
         umbrochener Satz als drei Kästen untereinander da. */
      const vorher = raus[raus.length - 1];
      if (vorher && vorher.art === 'merksatz') {
        vorher.text += ' ' + z.slice(2).trim();
      } else {
        raus.push({ art: 'merksatz', text: z.slice(2).trim() });
      }
      continue;
    }
    if (z.startsWith('• ') || z.startsWith('- ')) {
      absatzZu();
      punkte.push(z.slice(2).trim());
      continue;
    }
    /* Eine Folgezeile eines Punktes gehört zu ihm und nicht in einen neuen
       Absatz: im Text sind die Punkte eingerückt umbrochen. */
    if (punkte.length && /^\s/.test(roh)) {
      punkte[punkte.length - 1] += ' ' + z;
      continue;
    }
    punkteZu();
    absatz.push(z);
  }
  absatzZu(); punkteZu();
  return raus;
}

/**
 * FETT IST DIE FÜNFTE MARKE, UND SIE STEHT INNERHALB EINER ZEILE.
 *
 * **DIE STERNE STANDEN ROH AUF DEM BILDSCHIRM.** In zweiunddreissig von
 * neunundvierzig Kapiteln: wer sie geschrieben hat, meinte Hervorhebung - der
 * Zeichner kannte nur vier Marken, und alle vier gelten am ZEILENANFANG.
 * Innerhalb einer Zeile gab es keine, also blieb der Stern stehen.
 *
 * **DIE STERNE WERDEN NICHT AUS DEN DATEN GENOMMEN, SONDERN GELESEN.** Ein
 * Kapitel, das ein Mensch gepflegt hat, wird von keiner Migration angefasst
 * (`handbuch.von_hand`) - und wer im Betrieb ein Kapitel schreibt, tippt
 * ohnehin Sterne, weil das jeder kennt. Eine Migration, die sie herausnimmt,
 * müsste bei jedem neuen Kapitel wieder laufen.
 *
 * **UND DIE MARKE DARF UMBRECHEN, WIE JEDE ANDERE AUCH.** Sie wird auf den
 * fertigen ABSATZ angewendet und nicht auf die rohe Zeile: im Kapiteltext
 * steht ein Satz über drei Zeilen, und ein Stern am Anfang der einen und einer
 * am Ende der dritten sind EIN Paar. Bis .115 stand die Regel am Zeichner und
 * damit an der Zeile - in acht Kapiteln lief ein Paar über einen Umbruch, und
 * dort standen die Sterne weiter da. Das ist dieselbe Lehre wie bei der
 * Bildunterschrift am 26.08.2026, nur an der fünften Marke.
 *
 * EIN EINZELNER STERN BLEIBT EIN STERN. Gesucht wird ein PAAR mit Text
 * dazwischen; „5 * 3" und „Fussnote *" gehen unverändert durch.
 */
export function fettTeile(zeile: string): { text: string; fett: boolean }[] {
  const teile = String(zeile ?? '').split(/\*\*([^*]+?)\*\*/g);
  /* Der Split legt Text und Fettes abwechselnd ab: gerade Stellen sind Text,
     ungerade das, was zwischen den Sternen stand. */
  return teile.map((t, i) => ({ text: t, fett: i % 2 === 1 }));
}

/**
 * WAS EIN MENSCH AM ENDE LIEST - ohne Marken, ohne Sterne.
 *
 * Der Prüfstand braucht genau das: **eine Aussage über einen Bildschirm, die
 * man nicht am fertigen Text nachsehen kann, ist eine Behauptung.** Wer im
 * Quelltext nach `**` sucht, findet die Sterne IM KAPITELTEXT - und die sollen
 * dort stehen. Die Frage ist, ob nach dem Zeichnen noch einer übrig ist.
 */
export function gelesenerText(text: string): string {
  const raus: string[] = [];
  for (const s of zerlege(text)) {
    switch (s.art) {
      case 'ueberschrift':
      case 'merksatz':
      case 'absatz':
        raus.push(fettTeile(s.text).map((t) => t.text).join(''));
        break;
      case 'punkte':
        for (const p of s.punkte) {
          raus.push(fettTeile(p).map((t) => t.text).join(''));
        }
        break;
      case 'bild':
        raus.push(s.unter);
        break;
      /* Ein Ablaufplan steht im Programmcode und nicht im Kapiteltext. */
      case 'ablauf': break;
    }
  }
  return raus.join('\n');
}

/**
 * IST DIESES KAPITEL EINE ANLEITUNG?
 *
 * ORIGINALTEXT MARVIN (05.09.2026)
 * > „es ist keine anleitung und es fehlen auch bilder"
 *
 * **ER HAT RECHT, UND ES WAR ABZUZÄHLEN: von neunundvierzig Kapiteln trug
 * genau EINES nummerierte Schritte.** Der Rest erklärt, WARUM etwas so ist -
 * gut geschrieben und trotzdem nicht das, wonach jemand sucht, der vor dem
 * Bildschirm sitzt und nicht weiterweiss.
 *
 * **EINE ANLEITUNG IST HIER EINE ÜBERSCHRIFT UND EINE FOLGE.** Der Kasten
 * „So geht es" mit nummerierten Punkten darunter; die Nummer ist der ganze
 * Unterschied zu einer Aufzählung, denn sie sagt, was ZUERST kommt. Was
 * WARUM so ist, bleibt daneben stehen: das eine ersetzt das andere nicht.
 */
export const SCHRITTE_UEBERSCHRIFT = 'So geht es';

export function hatSchritte(text: string): boolean {
  const st = zerlege(text);
  for (let i = 0; i < st.length; i++) {
    const s = st[i];
    if (s.art !== 'ueberschrift') continue;
    if (!s.text.toLowerCase().startsWith(SCHRITTE_UEBERSCHRIFT.toLowerCase())) continue;
    /* EINE ÜBERSCHRIFT OHNE SCHRITTE DARUNTER IST KEINE ANLEITUNG. Genau die
       Sorte Abschnitt, die man beim Nachtragen anlegt und nicht füllt. */
    const naechste = st[i + 1];
    if (naechste?.art === 'punkte' && naechste.punkte.length >= 2) return true;
  }
  return false;
}
